import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import CurrencyFormat from 'react-currency-format';
import { generateUrlSearchParams } from '@/services/googleMaps';
import {
  PurchaseSourceEnum,
  IPurchaseTransaction,
  TransactionTypeEnum,
  IEmergePayTransaction,
  TGetTransactionDetailsData,
  TAttachment
} from '@/services/types/TransactionData';
import { ExitIcon, InternetIcon, MapPinIcon } from '@/components/Icons';
import Button from '@/components/Button';
import cn from '@/utils/cn';

// HOME PAGE SPECIFIC HOOKS/TYPES/UTILS
import useHomeContext from '@/hooks/useHomeContext';
import {
  AllPossibleTransactionStates,
  getTransactionStatus
} from '../../utils/getTransactionStatus';
import AvatarWithIcon from '../AvatarWithIcon';
import { usePlace } from '../../hooks/usePlace';
import { getTransactionType } from '../../utils/getTransactionType';
import { getTransactionAmount } from '../../utils/getTransactionAmount';
import { isCredit } from '../../utils/isCredit';
import { renderDetailFooter } from './renderDetailFooter';
import { TGeolocation } from '../../types/Place';
import { getAmountSign } from '../../utils/getAmountSign';
import { useClickOutside } from '@/hooks/useClickOutside';
import AttachmentModal from '../AttachmentModal';
import RemoveAttachmentConfirmModal from '../RemoveAttachmentConfirmModal';
import {
  deleteTransactionAttachment,
  getTransactionDetails,
  updateTransactionNote
} from '@/services/transactionHistory';
import { NotificationType } from '@/context/types/Notification';
import { TDeleteAttachmentData } from '../../types/DeleteAttachmentData';
import { getEmergePaySubtext } from '../../utils/getEmergePaySubtext';
import useAuth from '@/hooks/useAuth';
import { renderTransactionDrawerDetails } from './renderTransactionDrawerDetails';
import { getHumanReadableReceiptExemption } from '../../utils/getHumanReadableReceiptExemption';
import useReceiptExemptionReason from '../../hooks/useReceiptExemptionReason';
import { isAttachmentRequired } from '../../utils/isReceiptRequired';
import { useTransactionNotes } from '../../hooks/useTransactionNotes';
import { debounce } from '@/hooks/debounce';

const CloseButtonContainerClass = cn([
  'text-dark-navy-30',
  'bg-transparent',
  'hover:bg-gray-200',
  'hover:text-gray-900',
  'rounded-lg',
  'text-sm',
  'w-8',
  'h-8',
  'absolute',
  'top-7',
  'end-7',
  'inline-flex',
  'items-center',
  'justify-center'
]);

const PurchaseHeaderClass = cn('h-72 w-auto rounded-t-lg relative overflow-hidden');
const showMap = (position: TGeolocation) => {
  const hasGeolocation = position.lat && position.lng;
  return (
    <div className={cn(PurchaseHeaderClass, !hasGeolocation ? 'background-drawer-header' : '')}>
      {hasGeolocation ? (
        <img
          className="map-img w-full h-full"
          src={`https://maps.googleapis.com/maps/api/staticmap?${generateUrlSearchParams(position.lat!, position.lng!)}`}
        />
      ) : (
        <div className="relative" />
      )}
    </div>
  );
};

const TransactionDetailsDrawer = () => {
  // CONTEXT HOOKS
  const { customer, accountType } = useAuth()!;
  const {
    setCurrentPageTransactions,
    selectedTransaction,
    setSelectedTransaction,
    setError,
    triggerNotification
  } = useHomeContext()!;

  // REFS
  const transactionDetailsRef = useRef<HTMLDivElement | null>(null);

  // LOCAL HOOKS
  const { receiptExemptionReason, setReceiptExemptionReason, handleReceiptExemptionReasonChanged } =
    useReceiptExemptionReason();
  const { txnNote, handleInitValue, handleChangeNotes, removeNoteFromState } =
    useTransactionNotes();
  const [txnDetails, setTxnDetails] = useState<TGetTransactionDetailsData | null>(null);
  const [amount, setAmount] = useState<ReturnType<typeof getTransactionAmount>>();
  const [deleteAttachmentData, setDeleteAttachmentData] = useState<
    TDeleteAttachmentData | undefined
  >(undefined);
  const [location, setLocation] = useState<TGeolocation>({
    lat: null,
    lng: null
  });
  const [placeDetails, setPlaceDetails] = useState<{
    website?: string | null;
    address?: string | null;
  }>({ website: null, address: null });
  const [showAttachment, setShowAttachment] = useState(true);
  const [isNoteSaved, setIsNoteSaved] = useState(false);
  const { getPlace } = usePlace();
  const [hadFetch, setHadFetch] = useState(false);

  useClickOutside(transactionDetailsRef, () => {
    handleOnClose();
  });

  useEffect(() => {
    const getTxnDetails = async () => {
      const { data } = await getTransactionDetails(selectedTransaction!.data.id);

      if (data) {
        const selectedTxn = selectedTransaction!;

        setTxnDetails(data);
        setSelectedTransaction((prev) => {
          return {
            ...prev!,
            details: data
          };
        });

        if (txnNote === '') {
          handleInitValue(data?.note);
        }

        setAmount(getTransactionAmount(selectedTxn));
        if (selectedTxn.type === 'purchase') {
          const purchaseTransaction = selectedTxn.data as IPurchaseTransaction;
          if (
            purchaseTransaction.source === PurchaseSourceEnum.ONLINE ||
            !purchaseTransaction.place
          ) {
            setLocation({ lat: null, lng: null });
          } else {
            retrievePlace(purchaseTransaction.place.id);
          }

          if (purchaseTransaction.receiptExemptionReason) {
            setReceiptExemptionReason({
              value: purchaseTransaction.receiptExemptionReason,
              label: getHumanReadableReceiptExemption(purchaseTransaction.receiptExemptionReason)
            });
          }

          setShowAttachment(!purchaseTransaction.receiptExemptionReason);
        }
      }
    };

    if (selectedTransaction && !hadFetch) {
      setHadFetch(true);
      getTxnDetails();
    }
  }, [selectedTransaction, hadFetch, txnNote]);

  // HANDLERS

  const handleOnClose = () => {
    removeNoteFromState();
    setIsNoteSaved(false);
    setSelectedTransaction(undefined);
    setAmount(undefined);
    setReceiptExemptionReason(undefined);
    setShowAttachment(true);
    setHadFetch(false);
  };

  const handleShowAttachmentChanged = () => {
    setShowAttachment((prev) => !prev);
  };

  const removeAttachment = async (transactionId: string, attachmentId: string) => {
    setDeleteAttachmentData(undefined);
    await deleteTransactionAttachment(transactionId, attachmentId)
      .then(() => {
        const updatedAttachments = txnDetails!.attachments.filter(
          (attachment) => attachment.id !== attachmentId
        );
        const hasRemainingAttachments = updatedAttachments.length > 0;

        setTxnDetails((prev) => ({
          ...prev!,
          attachments: updatedAttachments
        }));

        // Update the transaction from the list
        setCurrentPageTransactions((currentTxns) =>
          currentTxns.map((txn) => {
            return txn.data.id === transactionId
              ? {
                  ...txn,
                  data: {
                    ...txn.data,
                    hasAttachments: hasRemainingAttachments
                  }
                }
              : txn;
          })
        );

        // update the receipt state in the transaction details
        setSelectedTransaction((prev) =>
          prev
            ? {
                ...prev,
                data: {
                  ...prev.data,
                  hasAttachments: hasRemainingAttachments
                },
                details: prev.details
                  ? {
                      ...prev.details,
                      attachments: updatedAttachments
                    }
                  : prev.details
              }
            : prev
        );

        triggerNotification(NotificationType.ATTACHMENT_REMOVED);
      })
      .catch(() => {
        setError(
          `An error has occurred deleting the attachment. Please contact support if the issue persists.`
        );
      });
  };

  const handleDeleteAttachment = (data: TDeleteAttachmentData) => {
    setDeleteAttachmentData(data);
  };

  const isDeclined = (status: AllPossibleTransactionStates) =>
    getTransactionStatus(status) === 'declined';

  const retrievePlace = async (placeId: string) => {
    const res = await getPlace(placeId);
    if (res) {
      setLocation({ lat: res.latitude, lng: res.longitude });
      setPlaceDetails({
        website: res.website,
        address: res.address
      });
    }
  };

  const finishUploadHandler = (attachment: TAttachment) => {
    setReceiptExemptionReason(undefined);

    setTxnDetails((prev) =>
      prev
        ? {
            ...prev,
            attachments: [...(prev?.attachments ?? []), attachment]
          }
        : prev
    );

    setSelectedTransaction((prev) =>
      prev
        ? {
            ...prev,
            details: {
              ...prev.details!,
              attachments: [...(prev?.details?.attachments ?? []), attachment]
            }
          }
        : prev
    );
  };

  // Saves the note to backend after the debounce time
  const handleSaveTransactionNoteChange = useCallback(
    debounce(async (newNote: string) => {
      await updateTransactionNote(selectedTransaction!.data.id, { note: newNote }).then(() => {
        const transactionId = selectedTransaction?.data.id;

        setCurrentPageTransactions((currentTxns) =>
          currentTxns.map((txn) => {
            return txn.data.id === transactionId
              ? {
                  ...txn,
                  details: {
                    ...txn.details!,
                    note: newNote
                  }
                }
              : txn;
          })
        );

        // Update selected transaction to automatically reflect the new change in the transaction drawer
        setSelectedTransaction((prev) =>
          prev
            ? {
                ...prev,
                details: {
                  ...prev.details!,
                  note: newNote
                }
              }
            : prev
        );

        setIsNoteSaved(true);
      });
    }, 1000),
    [selectedTransaction]
  );

  // shallow update the note
  const handleUpdateNote = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleChangeNotes(e);
      setIsNoteSaved(false);
      handleSaveTransactionNoteChange(e.target.value);
    },
    [selectedTransaction]
  );

  return (
    <>
      {selectedTransaction && customer && amount ? (
        <div
          ref={transactionDetailsRef}
          id="drawer-right-example"
          className="drawer-container fixed top-0 right-0 z-90 w-2/5 h-screen p-4 overflow-y-auto bg-emerge-base-3 tablet:w-3/4 tablet:shadow-xl"
          aria-labelledby="drawer-right-label">
          <AttachmentModal />
          <RemoveAttachmentConfirmModal
            isOpen={deleteAttachmentData !== undefined}
            onClose={() => setDeleteAttachmentData(undefined)}
            attachmentData={deleteAttachmentData}
            onConfirm={() =>
              removeAttachment(
                deleteAttachmentData!.transactionId,
                deleteAttachmentData!.attachmentId
              )
            }
          />
          <div className="p-4">
            <div>
              <button type="button" onClick={handleOnClose} className={CloseButtonContainerClass}>
                <ExitIcon />
                <span className="sr-only">Close menu</span>
              </button>
              <h4 className="font-semibold text-emerge-navy">Transaction details</h4>
            </div>
            <div className="bg-white rounded-lg shadow-sm h-auto mt-2">
              {/* header */}
              {selectedTransaction.type === 'purchase' && location ? (
                showMap(location)
              ) : (
                <div className="h-12" />
              )}
              {/* transaction icon */}
              <div className="absolute">
                <div className="relative -top-7 left-4">
                  <AvatarWithIcon
                    showReceiptIndicator={true}
                    className="w-9 h-9 bg-white rounded-full"
                    data={selectedTransaction}
                    transactionStatus={getTransactionStatus(selectedTransaction.data.state)}
                  />
                </div>
              </div>

              <div className="p-4 flex flex-col gap-y-4">
                {/* transaction overview */}
                <div>
                  <h3>{selectedTransaction.data.title}</h3>
                  {isDeclined(selectedTransaction.data.state) ? (
                    <p className="text-xs text-error-darkred capitalize">
                      {selectedTransaction.data.state}
                    </p>
                  ) : (
                    <p className="text-xs text-emerge-navy-2 capitalize">
                      {getTransactionType(selectedTransaction)}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-y-4">
                  <div className="flex flex-col gap-y-2">
                    {isDeclined(selectedTransaction.data.state) ? (
                      <span className="text-error-darkred text-4xl line-through">
                        <CurrencyFormat
                          value={Number(amount.amount).toFixed(2)}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'$'}
                          decimalScale={2}
                        />
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'text-4xl',
                          'font-medium',
                          'text-emerge-navy',
                          isCredit(amount.sign) ? 'text-dark-teal-30' : ''
                        )}>
                        {getAmountSign(amount.sign)}
                        <CurrencyFormat
                          id="currency"
                          value={Number(amount.amount).toFixed(2)}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'$'}
                          decimalScale={2}
                        />
                      </span>
                    )}
                    {selectedTransaction.type === TransactionTypeEnum.Purchase &&
                    isAttachmentRequired(selectedTransaction.data as IPurchaseTransaction) ? (
                      <span className="text-sm font-normal text-emerge-purple">
                        A receipt is required for this transaction
                      </span>
                    ) : null}

                    {placeDetails?.address ? (
                      <span className="text-xs flex flex-row gap-x-1 text-emerge-navy-2">
                        <MapPinIcon size="sm" className="text-emerge-navy-2" />
                        {placeDetails.address}
                      </span>
                    ) : (
                      ''
                    )}

                    {selectedTransaction.type === TransactionTypeEnum.EmergePay
                      ? getEmergePaySubtext(selectedTransaction.data as IEmergePayTransaction)
                      : ''}
                  </div>

                  {placeDetails.website ? (
                    <Button
                      size="md"
                      onClick={() => window.open(placeDetails.website!, '_blank')}
                      childrenClassName="gap-1 px-3 text-xs">
                      <InternetIcon size="sm" />
                      <span>Website</span>
                    </Button>
                  ) : (
                    ''
                  )}
                </div>

                {/* transaction information */}
                <div>
                  <table className="w-full text-sm text-left text-emerge-navy-2">
                    <thead>
                      <tr className="py-4">
                        <th className="py-4 text-emerge-navy-2 border-b-2 border-aux-gray font-medium">
                          Transaction information
                        </th>
                        <th className="py-4 border-b-2 border-aux-gray" />
                      </tr>
                    </thead>
                    <tbody>
                      {renderTransactionDrawerDetails(
                        accountType!,
                        selectedTransaction,
                        selectedTransaction.details!
                      )}
                    </tbody>
                  </table>
                </div>

                {/* transaction footer */}
                {renderDetailFooter({
                  note: txnNote,
                  isNoteSaved,
                  onChangeNote: handleUpdateNote,
                  transaction: selectedTransaction,
                  onDeleteAttachment: handleDeleteAttachment,
                  showAttachment,
                  onShowAttachmentChanged: handleShowAttachmentChanged,
                  receiptExemptionReason: receiptExemptionReason,
                  onReceiptExemptionReasonSelected: (option) =>
                    handleReceiptExemptionReasonChanged(selectedTransaction.data.id, option),
                  onFinishAttachmentUpload: finishUploadHandler
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default TransactionDetailsDrawer;
